import React from 'react';
import { Button, Card, Space, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

// The shape every settings card takes, so eleven of them read as one page:
// a small card header carrying the Save button, one collapsed line of
// explanation, then label-left / control-right rows.

export const SettingsCard: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  extra?: React.ReactNode;
  loading?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ title, icon, extra, loading, style, children }) => (
  <Card
    size="small"
    loading={loading}
    title={<Space size={6}>{icon}{title}</Space>}
    extra={extra}
    style={{ marginBottom: 16, ...style }}
  >
    {children}
  </Card>
);

// The long explanations are worth keeping and not worth four lines of the
// viewport: one line, and the rest a click away.
export const CardHint: React.FC<{ children: React.ReactNode; rows?: number }> = ({ children, rows = 1 }) => (
  <Paragraph
    type="secondary"
    style={{ fontSize: 12, marginBottom: 10 }}
    ellipsis={{ rows, expandable: 'collapsible', symbol: (expanded) => (expanded ? 'less' : 'more') }}
  >
    {children}
  </Paragraph>
);

// `range` is the allowed span and default of a numeric control. It belongs
// beside the box the operator types into, not three lines up in prose: the
// question "what may I put here" is asked with the cursor already in the field.
export const Field: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  range?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, range, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
    <div style={{ flex: 1, minWidth: 0, paddingTop: 3 }}>
      <Text style={{ fontSize: 13 }}>{label}</Text>
      {hint && <div><Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text></div>}
    </div>
    <div style={{ flexShrink: 0, textAlign: 'right' }}>
      {children}
      {range && (
        <div style={{ marginTop: 2 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{range}</Text>
        </div>
      )}
    </div>
  </div>
);

export const SaveButton: React.FC<{ onClick: () => void; loading?: boolean }> = ({ onClick, loading }) => (
  <Button type="primary" size="small" icon={<SaveOutlined />} onClick={onClick} loading={loading}>
    Save
  </Button>
);

// One width for every value control on the page.
export const CONTROL_WIDTH = 168;
